import { ADMIN_HOME_V2_REF } from 'bundles/enterprise-admin-home/constants';

/**
 * Checks if a given href is an external URL (http://, https://, or protocol-relative //).
 *
 * @param href - The URL string to check
 * @returns true if the href is external, false otherwise
 *
 * @example
 * isExternalHref('https://example.com') // true
 * isExternalHref('//example.com') // true
 * isExternalHref('/admin/home') // false
 */
export const isExternalHref = (href: string): boolean => /^(https?:)?\/\//i.test(href);

/**
 * Page-specific query params that must not be carried onto sibling nav links. These describe the
 * state of a single page (e.g. which collection the detail page is showing), so leaking them onto
 * other destinations is wrong — navigating from `.../curated-catalog/detail?collectionId=x` to the
 * Curated catalog listing would otherwise keep the stale `?collectionId=x`.
 */
const NON_PERSISTENT_PARAMS = new Set(['collectionId']);

/**
 * Computes the search query string to carry over during navigation.
 *
 * Removes the `ref=ADMIN_HOME_V2_REF` parameter if present (internal navigation tracking) and any
 * page-specific params in {@link NON_PERSISTENT_PARAMS}. All other parameters are preserved.
 *
 * @param locationSearch - The current location's search string (e.g., '?ref=admin_home_v2&foo=bar')
 * @returns Query string to append to links (e.g., '?foo=bar'), or empty string if no params remain
 *
 * @example
 * computeSearchCarry('?ref=admin_home_v2&foo=bar') // Returns: '?foo=bar'
 * computeSearchCarry('?ref=admin_home_v2') // Returns: ''
 * computeSearchCarry('?collectionId=abc&foo=bar') // Returns: '?foo=bar'
 * computeSearchCarry('') // Returns: ''
 */
export const computeSearchCarry = (locationSearch: string): string => {
  if (!locationSearch) return '';

  // Remove the leading '?' if present
  const search = locationSearch.startsWith('?') ? locationSearch.slice(1) : locationSearch;
  if (!search) return '';

  // Parse query parameters manually to avoid URLSearchParams issues in test environments
  const params = new Map<string, string[]>();
  search.split('&').forEach((param) => {
    const [key, value] = param.split('=');
    if (key) {
      if (!params.has(key)) {
        params.set(key, []);
      }
      params.get(key)!.push(value || '');
    }
  });

  // Remove the ref parameter if it matches ADMIN_HOME_V2_REF
  if (params.has('ref')) {
    const refValues = params.get('ref')!;
    if (refValues.length === 1 && refValues[0] === ADMIN_HOME_V2_REF) {
      params.delete('ref');
    }
  }

  // Drop page-specific params so they don't stick to other nav destinations.
  NON_PERSISTENT_PARAMS.forEach((key) => params.delete(key));

  // Reconstruct the query string
  if (params.size === 0) return '';

  const queryParts: string[] = [];
  params.forEach((values, key) => {
    values.forEach((value) => {
      queryParts.push(value ? `${key}=${value}` : key);
    });
  });

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
};
